import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Mail, Send, Clock, CheckCircle, AlertTriangle, RotateCcw, Eye,
  Search, Filter, ArrowRight, ExternalLink, XCircle, Inbox, Users
} from "lucide-react";

const STATUS_MAP = {
  Draft: { color: "bg-slate-800 text-slate-400 border-slate-700", icon: Mail },
  Sent: { color: "bg-blue-500/10 text-blue-400 border-blue-500/20", icon: Send },
  Viewed: { color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20", icon: Eye },
  InProgress: { color: "bg-amber-500/10 text-amber-400 border-amber-500/20", icon: Clock },
  Submitted: { color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", icon: CheckCircle },
  Accepted: { color: "bg-green-500/10 text-green-400 border-green-500/20", icon: CheckCircle },
  Revised: { color: "bg-orange-500/10 text-orange-400 border-orange-500/20", icon: RotateCcw },
};

function TimeLeft({ expiresAt }) {
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return <span className="text-xs text-red-400 font-medium">Expired</span>;
  const hrs = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const urgent = hrs < 6;
  return <span className={`text-xs font-medium ${urgent ? "text-amber-400" : "text-slate-400"}`}>{hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`}</span>;
}

export default function PricingRequestsDashboard() {
  const { isDirector } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDiscipline, setFilterDiscipline] = useState("all");

  useEffect(() => {
    API.get("/pricing-requests/all")
      .then(r => setData(r.data))
      .catch(() => toast.error("Failed to load pricing requests"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!data?.requests) return [];
    return data.requests.filter(r => {
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (filterDiscipline !== "all" && r.discipline !== filterDiscipline) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          r.recipient_name?.toLowerCase().includes(q) ||
          r.recipient_email?.toLowerCase().includes(q) ||
          r.project_name?.toLowerCase().includes(q) ||
          r.client_name?.toLowerCase().includes(q) ||
          r.discipline?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [data, search, filterStatus, filterDiscipline]);

  const handleResend = async (id) => {
    try {
      await API.post(`/pricing-requests/${id}/send`);
      toast.success("Request resent");
      const r = await API.get("/pricing-requests/all");
      setData(r.data);
    } catch { toast.error("Failed to resend"); }
  };

  const handleExtend = async (id) => {
    try {
      await API.post(`/pricing-requests/${id}/extend`, { extra_hours: 48 });
      toast.success("Extended by 48 hours");
      const r = await API.get("/pricing-requests/all");
      setData(r.data);
    } catch { toast.error("Failed to extend"); }
  };

  const stats = data?.stats || {};
  const disciplines = Object.keys(stats.by_discipline || {});
  const statuses = Object.keys(stats.by_status || {});

  if (loading) return <DashboardSkeleton />;

  const kpis = [
    { label: "Total Requests", value: stats.total || 0, icon: Mail, color: "text-blue-400", bg: "bg-blue-500/10" },
    { label: "Awaiting Response", value: stats.pending || 0, icon: Clock, color: "text-amber-400", bg: "bg-amber-500/10" },
    { label: "Submitted", value: stats.submitted || 0, icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-500/10" },
    { label: "Overdue", value: stats.overdue || 0, icon: AlertTriangle, color: stats.overdue > 0 ? "text-red-400" : "text-slate-500", bg: stats.overdue > 0 ? "bg-red-500/10" : "bg-slate-800" },
  ];

  return (
    <div className="space-y-6" data-testid="pricing-requests-dashboard">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">Pricing Requests</h1>
        <p className="text-sm text-slate-500 mt-1">Manage effort pro formas across all projects</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map(kpi => (
          <Card key={kpi.label} className="bg-slate-900 border-slate-800" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, "-")}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-heading font-medium uppercase tracking-wider text-slate-500">{kpi.label}</p>
                  <p className="font-mono text-2xl font-bold mt-1 text-slate-100">{kpi.value}</p>
                </div>
                <div className={`w-9 h-9 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon className={`w-4 h-4 ${kpi.color}`} strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Discipline breakdown */}
      {disciplines.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {disciplines.map(d => (
            <Badge key={d} variant="outline" className="text-xs border-slate-700 text-slate-400 px-3 py-1 cursor-pointer hover:border-blue-500/40 hover:text-blue-400 transition-colors"
              onClick={() => setFilterDiscipline(filterDiscipline === d ? "all" : d)} data-testid={`filter-discipline-${d}`}>
              {d}: {stats.by_discipline[d]}
              {filterDiscipline === d && <XCircle className="w-3 h-3 ml-1" />}
            </Badge>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
            <Input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, project, or discipline..."
              className="pl-9 h-9 bg-slate-800 border-slate-700 text-slate-200 text-sm"
              data-testid="search-requests"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[160px] h-9 bg-slate-800 border-slate-700 text-slate-200 text-sm" data-testid="filter-status">
              <Filter className="w-3.5 h-3.5 mr-1.5 text-slate-500" />
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {statuses.map(s => <SelectItem key={s} value={s}>{s} ({stats.by_status[s]})</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Requests Table */}
      {filtered.length === 0 ? (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="p-12 text-center">
            <Inbox className="w-12 h-12 text-slate-700 mx-auto mb-4" />
            <p className="text-sm text-slate-400">{data?.requests?.length ? "No requests match your filters" : "No pricing requests yet"}</p>
            <p className="text-xs text-slate-600 mt-1">Create pricing requests from within a project's Requests tab</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-800/30 border-slate-800">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 px-4 w-10">#</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 px-3">Recipient</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 px-3">Project</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 px-3">Discipline</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 px-3 text-center">Items</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 px-3">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 px-3">Time Left</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 px-3">Sent</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 px-3 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((req, idx) => {
                    const sm = STATUS_MAP[req.status] || STATUS_MAP.Draft;
                    const Icon = sm.icon;
                    const isExpired = req.expires_at && new Date(req.expires_at) < new Date();
                    const hasResponse = !!req.response;
                    return (
                      <TableRow key={req.id} className="border-slate-800/50 hover:bg-slate-800/20" data-testid={`request-row-${req.id}`}>
                        <TableCell className="px-4 py-2.5 text-xs text-slate-600 font-mono">{idx + 1}</TableCell>
                        <TableCell className="px-3 py-2.5">
                          <div>
                            <p className="text-sm font-medium text-slate-200">{req.recipient_name}</p>
                            <p className="text-xs text-slate-500">{req.recipient_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="px-3 py-2.5">
                          <Link to={`/projects/${req.project_id}?tab=requests&requestId=${req.id}`} className="text-sm text-blue-400 hover:text-blue-300 hover:underline" data-testid={`goto-project-${req.id}`}>
                            {req.project_name}
                          </Link>
                          <p className="text-xs text-slate-500">{req.client_name}</p>
                        </TableCell>
                        <TableCell className="px-3 py-2.5">
                          <Badge variant="outline" className="text-xs border-slate-700 text-slate-300">{req.discipline}</Badge>
                        </TableCell>
                        <TableCell className="px-3 py-2.5 text-center">
                          <span className="text-sm text-slate-300 font-mono">{req.selected_item_ids?.length || 0}</span>
                        </TableCell>
                        <TableCell className="px-3 py-2.5">
                          <Badge variant="outline" className={`text-xs flex items-center gap-1 w-fit ${sm.color}`}>
                            <Icon className="w-3 h-3" />{req.status}
                          </Badge>
                          {req.scope_changes?.length > 0 && (
                            <Badge className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/20 mt-1 w-fit">Scope Changed</Badge>
                          )}
                        </TableCell>
                        <TableCell className="px-3 py-2.5">
                          {req.status !== "Draft" && req.status !== "Accepted" && req.expires_at ? (
                            isExpired ? <span className="text-xs text-red-400 font-medium">Expired</span> : <TimeLeft expiresAt={req.expires_at} />
                          ) : (
                            <span className="text-xs text-slate-600">—</span>
                          )}
                        </TableCell>
                        <TableCell className="px-3 py-2.5 text-xs text-slate-500 font-mono">
                          {req.sent_at ? new Date(req.sent_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}
                        </TableCell>
                        <TableCell className="px-3 py-2.5 text-right">
                          <div className="flex items-center gap-1.5 justify-end">
                            {req.status === "Draft" && (
                              <Button variant="outline" size="sm" className="h-7 text-xs border-blue-500/30 text-blue-400" onClick={() => handleResend(req.id)} data-testid={`send-${req.id}`}>
                                <Send className="w-3 h-3 mr-1" />Send
                              </Button>
                            )}
                            {["Sent", "Viewed"].includes(req.status) && isExpired && (
                              <Button variant="outline" size="sm" className="h-7 text-xs border-amber-500/30 text-amber-400" onClick={() => handleExtend(req.id)} data-testid={`extend-${req.id}`}>
                                <RotateCcw className="w-3 h-3 mr-1" />Extend
                              </Button>
                            )}
                            {["Sent", "Viewed", "InProgress"].includes(req.status) && !isExpired && (
                              <Button variant="outline" size="sm" className="h-7 text-xs border-slate-700 text-slate-400" onClick={() => handleResend(req.id)} data-testid={`resend-${req.id}`}>
                                <Send className="w-3 h-3 mr-1" />Resend
                              </Button>
                            )}
                            {hasResponse && (
                              <Link to={`/projects/${req.project_id}?tab=requests&requestId=${req.id}`}>
                                <Button variant="outline" size="sm" className="h-7 text-xs border-emerald-500/30 text-emerald-400" data-testid={`review-${req.id}`}>
                                  <Eye className="w-3 h-3 mr-1" />Review
                                </Button>
                              </Link>
                            )}
                            <Link to={`/projects/${req.project_id}?tab=requests&requestId=${req.id}`} data-testid={`goto-${req.id}`}>
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500">
                                <ExternalLink className="w-3 h-3" />
                              </Button>
                            </Link>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div><div className="h-10 w-56 bg-slate-800 rounded animate-pulse" /><div className="h-4 w-80 bg-slate-800/50 rounded animate-pulse mt-2" /></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[1,2,3,4].map(i => <div key={i} className="h-20 bg-slate-900 border border-slate-800 rounded-lg animate-pulse" />)}</div>
      <div className="h-72 bg-slate-900 border border-slate-800 rounded-lg animate-pulse" />
    </div>
  );
}
